import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 三角函数波形动画
 * 
 * 核心效果：
 * 1. 展示 sin、cos、tan 波形变化
 * 2. 可视化周期、振幅、相位概念
 * 3. 实时绘制波形曲线
 * 4. 适合：数学教育、物理教学
 */

interface TrigonometryWaveProps {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  showSin?: boolean;
  showCos?: boolean;
  showTan?: boolean;
  amplitude?: number;
  frequency?: number;
  phase?: number;
}

const DEFAULT_PROPS: Required<TrigonometryWaveProps> = {
  primaryColor: '#00d9ff',
  secondaryColor: '#ff6b6b',
  accentColor: '#ffd93d',
  backgroundColor: '#0a0e27',
  showSin: true,
  showCos: true,
  showTan: false,
  amplitude: 1,
  frequency: 1,
  phase: 0,
};

export const TrigonometryWaveAnimation: React.FC<TrigonometryWaveProps> = (props) => {
  const {
    primaryColor,
    secondaryColor,
    accentColor,
    backgroundColor,
    showSin,
    showCos,
    showTan,
    amplitude,
    frequency,
    phase,
  } = { ...DEFAULT_PROPS, ...props };
  
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const titleScale = spring({
    frame,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  // 波形绘制进度（持续整个动画）
  const waveProgress = interpolate(frame, [60, 300], [0, 1], { extrapolateRight: 'clamp' });

  // 参数说明出现动画
  const paramsOpacity = interpolate(frame, [320, 360], [0, 1], { extrapolateRight: 'clamp' });
  const paramsY = interpolate(frame, [320, 360], [30, 0], { extrapolateRight: 'clamp' });

  // 实时值动画
  const currentValueOpacity = interpolate(frame, [380, 420], [0, 1], { extrapolateRight: 'clamp' });

  // 计算波形点
  const graphWidth = width - 200;
  const graphHeight = 400;
  const centerY = (graphHeight + 100) / 2;
  const startX = 100;
  const endX = width - 100;
  const totalPoints = Math.floor(waveProgress * (graphWidth / 3));

  // 生成波形路径
  const generateWavePath = (
    func: (x: number) => number,
    color: string,
    offset: number = 0
  ): string => {
    if (totalPoints < 2) return '';
    
    const points: string[] = [];
    for (let i = 0; i < totalPoints; i++) {
      const x = startX + i * 3;
      const normalizedX = (i / (graphWidth / 3)) * 4 * Math.PI * frequency + phase;
      const y = centerY - func(normalizedX) * (graphHeight / 3) * amplitude;
      points.push(`${i === 0 ? 'M' : 'L'} ${x},${y}`);
    }
    return points.join(' ');
  };

  // 当前 X 值（用于显示实时值）
  const currentX = (waveProgress * 4 * Math.PI * frequency + phase) % (4 * Math.PI);
  const currentSin = Math.sin(currentX);
  const currentCos = Math.cos(currentX);
  const currentTan = Math.tan(currentX);

  return (
    <AbsoluteFill style={{
      background: backgroundColor,
      fontFamily: 'Inter, system-ui, sans-serif',
      color: '#e5e7eb',
      padding: '30px 60px 20px 60px',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* 标题 */}
      <div style={{
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `scale(${titleScale})`,
      }}>
        <h1 style={{
          fontSize: 72,
          fontWeight: 800,
          margin: 0,
          background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: 2,
        }}>
          三角函数波形
        </h1>
        <p style={{
          fontSize: 30,
          color: '#94a3b8',
          marginTop: 12,
        }}>
          Trigonometric Functions • sin cos tan
        </p>
      </div>

      {/* 波形绘制区域 */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
        <svg
          width={width - 120}
          height={graphHeight + 100}
          style={{ overflow: 'visible' }}
        >
          {/* 背景网格 */}
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect x={startX - 20} y={centerY - graphHeight / 2 - 20} width={graphWidth + 40} height={graphHeight + 40} fill="url(#grid)" />

          {/* 坐标轴 */}
          <line x1={startX - 20} y1={centerY} x2={endX + 20} y2={centerY} stroke="#4a5568" strokeWidth="2" />
          <line x1={startX} y1={centerY - graphHeight / 2} x2={startX} y2={centerY + graphHeight / 2} stroke="#4a5568" strokeWidth="2" />

          {/* Y 轴标签 */}
          <text x={startX - 60} y={centerY - graphHeight / 3} fill="#94a3b8" fontSize="26" textAnchor="end">1</text>
          <text x={startX - 60} y={centerY} fill="#94a3b8" fontSize="26" textAnchor="end">0</text>
          <text x={startX - 60} y={centerY + graphHeight / 3} fill="#94a3b8" fontSize="26" textAnchor="end">-1</text>

          {/* X 轴标签 */}
          <text x={startX + graphWidth / 8} y={centerY + graphHeight / 2 + 40} fill="#94a3b8" fontSize="24" textAnchor="middle">π/2</text>
          <text x={startX + graphWidth / 4} y={centerY + graphHeight / 2 + 40} fill="#94a3b8" fontSize="24" textAnchor="middle">π</text>
          <text x={startX + graphWidth * 3 / 8} y={centerY + graphHeight / 2 + 40} fill="#94a3b8" fontSize="24" textAnchor="middle">3π/2</text>
          <text x={startX + graphWidth / 2} y={centerY + graphHeight / 2 + 40} fill="#94a3b8" fontSize="24" textAnchor="middle">2π</text>

          {/* Sin 波形 */}
          {showSin && (
            <g>
              <path
                d={generateWavePath(Math.sin, primaryColor)}
                stroke={primaryColor}
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
              />
              {/* 当前点 */}
              {waveProgress > 0 && waveProgress < 1 && (
                <circle
                  cx={startX + totalPoints * 3}
                  cy={centerY - Math.sin(currentX) * (graphHeight / 3) * amplitude}
                  r="8"
                  fill={primaryColor}
                />
              )}
            </g>
          )}

          {/* Cos 波形 */}
          {showCos && (
            <g>
              <path
                d={generateWavePath(Math.cos, secondaryColor)}
                stroke={secondaryColor}
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
              />
              {/* 当前点 */}
              {waveProgress > 0 && waveProgress < 1 && (
                <circle
                  cx={startX + totalPoints * 3}
                  cy={centerY - Math.cos(currentX) * (graphHeight / 3) * amplitude}
                  r="8"
                  fill={secondaryColor}
                />
              )}
            </g>
          )}

          {/* Tan 波形（仅在参数开启时显示） */}
          {showTan && (
            <g opacity={0.5}>
              <path
                d={generateWavePath((x) => Math.max(-2, Math.min(2, Math.tan(x))), accentColor)}
                stroke={accentColor}
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="8,4"
              />
            </g>
          )}

          {/* 图例 */}
          <g transform={`translate(${endX - 150}, ${centerY - graphHeight / 2})`}>
            {showSin && (
              <g transform="translate(0, 0)">
                <line x1="0" y1="0" x2="40" y2="0" stroke={primaryColor} strokeWidth="4" />
                <text x="50" y="5" fill="#e5e7eb" fontSize="26">sin(x)</text>
              </g>
            )}
            {showCos && (
              <g transform="translate(0, 40)">
                <line x1="0" y1="0" x2="40" y2="0" stroke={secondaryColor} strokeWidth="4" />
                <text x="50" y="5" fill="#e5e7eb" fontSize="26">cos(x)</text>
              </g>
            )}
            {showTan && (
              <g transform="translate(0, 80)">
                <line x1="0" y1="0" x2="40" y2="0" stroke={accentColor} strokeWidth="3" strokeDasharray="8,4" />
                <text x="50" y="5" fill="#e5e7eb" fontSize="26">tan(x)</text>
              </g>
            )}
          </g>
        </svg>
      </div>

      {/* 参数说明 */}
      <div style={{
        display: 'flex',
        gap: 30,
        justifyContent: 'center',
        opacity: paramsOpacity,
        transform: `translateY(${paramsY}px)`,
      }}>
        <div style={{
          background: 'rgba(0, 217, 255, 0.1)',
          borderRadius: 15,
          padding: '20px 35px',
          border: `2px solid ${primaryColor}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 22, color: '#94a3b8', marginBottom: 8 }}>振幅 Amplitude</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: primaryColor }}>
            A = {amplitude.toFixed(1)}
          </div>
        </div>
        <div style={{
          background: 'rgba(255, 107, 107, 0.1)',
          borderRadius: 15,
          padding: '20px 35px',
          border: `2px solid ${secondaryColor}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 22, color: '#94a3b8', marginBottom: 8 }}>频率 Frequency</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: secondaryColor }}>
            f = {frequency.toFixed(1)}
          </div>
        </div>
        <div style={{
          background: 'rgba(255, 217, 61, 0.1)',
          borderRadius: 15,
          padding: '20px 35px',
          border: `2px solid ${accentColor}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 22, color: '#94a3b8', marginBottom: 8 }}>相位 Phase</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: accentColor }}>
            φ = {(phase / Math.PI).toFixed(2)}π
          </div>
        </div>
      </div>

      {/* 实时值显示 */}
      <div style={{
        display: 'flex',
        gap: 40,
        justifyContent: 'center',
        marginTop: 15,
        opacity: currentValueOpacity,
      }}>
        {showSin && (
          <div style={{
            background: 'rgba(0, 217, 255, 0.08)',
            borderRadius: 12,
            padding: '15px 30px',
            border: `2px solid rgba(0, 217, 255, 0.3)`,
          }}>
            <span style={{ color: primaryColor, fontSize: 26, fontWeight: 600 }}>sin(x) = </span>
            <span style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>{currentSin.toFixed(3)}</span>
          </div>
        )}
        {showCos && (
          <div style={{
            background: 'rgba(255, 107, 107, 0.08)',
            borderRadius: 12,
            padding: '15px 30px',
            border: `2px solid rgba(255, 107, 107, 0.3)`,
          }}>
            <span style={{ color: secondaryColor, fontSize: 26, fontWeight: 600 }}>cos(x) = </span>
            <span style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>{currentCos.toFixed(3)}</span>
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};

// 默认导出
export default TrigonometryWaveAnimation;
